import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, CheckCircle, XCircle, AlertCircle,
  Shield, Plus, X, AlertTriangle, Trash2, Zap, Link as LinkIcon, ShieldCheck, Pencil, Network,
} from 'lucide-react';
import {
  useAdminIdentityUser,
  useAdminRoles,
  useSetUserStatus,
  useUpdateUserProfile,
  useAssignRole,
  useRemoveRole,
  useSetOperatorLimits,
  useReassignOperator,
  useBrokerCurrent,
  useDeleteUser,
  useMe,
  useAdminUserSubscription,
  useAdminUserSubscriptionHistory,
  useAdminBillingPlans,
  useOperatorWallet,
  useAssignPlan,
  useUpdateUserValidity,
  useLicensePacks,
  useAssignLicensePack,
  useAdminStrategiesByUser,
  useAdminPositionSummary,
  useMarkDeploymentFlat,
  useDismissOrder,
  useMapBroker,
  useUnmapBroker,
  useBrokerProxyBinding,
  useBrokerMappingAvailability,
  useSupportContact,
  useStrategies,
  useMasterStrategyMappings,
  useCreateMasterMapping,
  useUpdateMasterMapping,
} from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { Spinner, SectionHeader } from '../../components/ui';
import { BrokerCredentialFields } from '../../components/broker/BrokerCredentialFields';
import { BrokerSupportNotice } from '../../components/broker/BrokerSupportNotice';
import { fmtDateTime, fmtDate } from '../../lib/utils';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';
import {
  BROKER_CONFIGS,
  BROKER_NAMES,
  getBrokerFields,
  shouldRenderBrokerClientId,
} from '../../config/brokerFields';
import {
  computeBrokerAvailabilityMap,
  isBrokerSelectable,
  LOADING_MESSAGE as BROKER_AVAILABILITY_LOADING_MESSAGE,
  GENERIC_UNAVAILABLE_MESSAGE as BROKER_AVAILABILITY_ERROR_MESSAGE,
  sanitizeBrokerMapErrorMessage,
} from '../broker/brokerMappingAvailability';
import {
  CLEANUP_IN_PROGRESS_NOTICE,
  CLEANUP_INCOMPLETE_NOTICE,
  MARK_FLAT_ACTION_EXPLANATIONS,
  MARK_FLAT_ACTION_LABELS,
  UNKNOWN_OUTCOME_NOTICE,
  createMarkFlatController,
  markFlatErrorMessage,
  requestableActions,
  resolveMarkFlatResponse,
  verificationNotice,
} from './markFlatRecovery';
import { OperatorCustomDomainSection } from './OperatorCustomDomainSection';

// ─── Role badge ────────────────────────────────────────────────────────────────
function RoleBadge({ role, onRemove }: { role: string; onRemove?: () => void }) {
  const colors: Record<string, string> = {
    super_admin: 'bg-amber/20 text-amber border-amber/40',
    admin:       'bg-warn/20 text-warn border-warn/40',
    operator:    'bg-blue-500/20 text-blue-400 border-blue-500/40',
    end_user:    'bg-bg-surface text-txt-muted border-bg-border',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-2xs font-mono ${colors[role] ?? colors.end_user}`}>
      {role}
      {onRemove && (
        <button onClick={onRemove} className="hover:text-bear transition-colors" title={`Remove ${role}`}>
          <X size={11} />
        </button>
      )}
    </span>
  );
}

// ─── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active:               'text-bull bg-bull/10 border-bull/30',
    blocked:              'text-bear bg-bear/10 border-bear/30',
    pending_verification: 'text-warn bg-warn/10 border-warn/30',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${map[status] ?? 'text-txt-muted border-bg-border'}`}>
      {status ?? '—'}
    </span>
  );
}

// ─── Broker credentials masked display ────────────────────────────────────────
function BrokerCredentialsMasked({ broker }: { broker: any }) {
  // Prefer credentials_masked (Phase 1.2); fall back to legacy api_key_masked / api_secret_masked
  const hasMasked = broker.credentials_masked && Object.keys(broker.credentials_masked).length > 0;
  const hiddenTokenFields = new Set(['access_token', 'feed_token', 'refresh_token', 'auth_token', 'jwtToken', 'jwt_token', 'token']);
  const renderCredentialValue = (key: string, val: string) => (
    hiddenTokenFields.has(key)
      ? (val ? 'active' : 'not set')
      : (val || 'configured')
  );
  return (
    <>
      {hasMasked
        ? Object.entries(broker.credentials_masked as Record<string, string>).map(([key, val]) => (
            <div key={key}>
              <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{key.replace(/_/g, ' ')}</p>
              <p className="font-mono text-txt-secondary mt-0.5 break-words">{renderCredentialValue(key, val)}</p>
            </div>
          ))
        : (
            <>
              {broker.api_key_masked && (
                <div>
                  <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">API Key</p>
                  <p className="font-mono text-txt-secondary mt-0.5">{broker.api_key_masked}</p>
                </div>
              )}
              {broker.api_secret_masked && (
                <div>
                  <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">API Secret</p>
                  <p className="font-mono text-txt-secondary mt-0.5">{broker.api_secret_masked}</p>
                </div>
              )}
            </>
          )
      }
      {'totp_key_configured' in broker && (
        <div>
          <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">TOTP Key</p>
          <p className={`font-mono mt-0.5 text-xs flex items-center gap-1 ${broker.totp_key_configured ? 'text-bull' : 'text-txt-muted'}`}>
            {broker.totp_key_configured
              ? <><CheckCircle size={11} /> configured</>
              : <><XCircle size={11} /> not set</>}
          </p>
        </div>
      )}
    </>
  );
}

// ─── Operator limits form ──────────────────────────────────────────────────────
function OperatorLimitsSection({ userId, limits, targetRoles }: { userId: number; limits: any; targetRoles: string[] }) {
  // All hooks must be called unconditionally before any early return
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const setLimits    = useSetOperatorLimits();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    max_users_creatable:    limits?.max_users_creatable    ?? '',
    max_credit_grant_total: limits?.max_credit_grant_total ?? '',
    allow_role_assignment:  limits?.allow_role_assignment  ?? false,
    allow_status_change:    limits?.allow_status_change    ?? false,
    allow_broker_remap:     limits?.allow_broker_remap     ?? false,
    allow_admin_role_assignment: limits?.allow_admin_role_assignment ?? false,
    allow_user_delete:      limits?.allow_user_delete      ?? false,
  });
  const [err, setErr] = useState('');

  // Hide entirely for plain end_user targets — limits are only meaningful for operator+
  const targetIsOperatorPlus = targetRoles.some((r) =>
    ['operator', 'admin', 'super_admin'].includes(r)
  );
  if (!targetIsOperatorPlus) return null;
  if (!limits && !isSuperAdmin) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    const payload: Record<string, any> = {
      allow_role_assignment:       form.allow_role_assignment,
      allow_status_change:         form.allow_status_change,
      allow_broker_remap:          form.allow_broker_remap,
      allow_admin_role_assignment: form.allow_admin_role_assignment,
      allow_user_delete:           form.allow_user_delete,
    };
    if (form.max_users_creatable !== '') payload.max_users_creatable = Number(form.max_users_creatable);
    if (form.max_credit_grant_total !== '') payload.max_credit_grant_total = Number(form.max_credit_grant_total);

    try {
      await setLimits.mutateAsync({ userId, payload });
      toast.success('Operator limits updated');
      setEditing(false);
    } catch (e: any) {
      setErr(e?.message || 'Failed to update limits.');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Operator Limits</h3>
        {isSuperAdmin && !editing && (
          <button onClick={() => setEditing(true)} className="text-2xs text-amber hover:underline">Edit</button>
        )}
      </div>

      {!limits && <p className="text-xs text-txt-muted">No operator limits set.</p>}

      {limits && !editing && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            ['Max Users Creatable', limits.max_users_creatable ?? 'Unlimited'],
            ['Max Credit Grant',    limits.max_credit_grant_total ?? 'Unlimited'],
            ['Used Credit Grant',   limits.used_credit_grant_total ?? 0],
          ].map(([label, val]) => (
            <div key={label as string}>
              <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{label}</p>
              <p className="font-mono text-txt-secondary mt-0.5">{val}</p>
            </div>
          ))}
          <div className="col-span-2 flex flex-wrap gap-2 mt-1">
            {[
              ['Role Assignment',       limits.allow_role_assignment],
              ['Status Change',         limits.allow_status_change],
              ['Broker Remap',          limits.allow_broker_remap],
              ['Admin Role Assignment', limits.allow_admin_role_assignment],
              ['User Delete',           limits.allow_user_delete],
            ].map(([label, val]) => (
              <span key={label as string} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-2xs font-mono ${val ? 'text-bull bg-bull/10 border-bull/30' : 'text-txt-muted border-bg-border'}`}>
                {val ? <CheckCircle size={10} /> : <XCircle size={10} />} {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {editing && isSuperAdmin && (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Max Users Creatable', field: 'max_users_creatable' },
              { label: 'Max Credit Grant',    field: 'max_credit_grant_total' },
            ].map(({ label, field }) => (
              <div key={field} className="space-y-1">
                <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</label>
                <input
                  type="number"
                  min={0}
                  value={(form as any)[field]}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  placeholder="Unlimited"
                  className="input-base"
                />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {[
              { label: 'Allow Role Assignment',       field: 'allow_role_assignment' },
              { label: 'Allow Status Change',         field: 'allow_status_change' },
              { label: 'Allow Broker Remap',          field: 'allow_broker_remap' },
              { label: 'Allow Admin Role Assignment', field: 'allow_admin_role_assignment' },
              { label: 'Allow User Delete',           field: 'allow_user_delete' },
            ].map(({ label, field }) => (
              <label key={field} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(form as any)[field]}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.checked }))}
                  className="accent-amber"
                />
                <span className="text-xs text-txt-secondary">{label}</span>
              </label>
            ))}
          </div>
          {err && <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{err}</p>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setEditing(false)} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={setLimits.isPending} className="btn-primary">
              {setLimits.isPending ? 'Saving...' : 'Save Limits'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Strategy Mapping section ─────────────────────────────────────────────────
function StrategyMappingSection({ userId }: { userId: number }) {
  const { data: strategies = [], isLoading, error } = useStrategies();
  const masters = (strategies as any[]).filter((s: any) => (
    s?.strategy_mode === 'MASTER_EXTERNAL' || s?.strategy_mode === 'MASTER_MANUAL'
  ));

  return (
    <div className="panel p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Network size={14} className="text-amber" />
        <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Strategy Mapping</h3>
      </div>
      {isLoading && <p className="text-xs text-txt-muted">Loading…</p>}
      {error && <p className="text-xs text-bear">Failed to load strategies</p>}
      {!isLoading && masters.length === 0 && (
        <p className="text-xs text-txt-muted">
          No master strategies yet. Enable one from the Strategy Builder to map it here.
        </p>
      )}
      {masters.length > 0 && (
        <div className="space-y-1.5">
          {masters.map((s: any) => (
            <StrategyMappingRow key={s.id} strategy={s} userId={userId} />
          ))}
        </div>
      )}
    </div>
  );
}

function StrategyMappingRow({ strategy, userId }: { strategy: any; userId: number }) {
  const strategyId = strategy.id;
  const isManualMaster = strategy?.strategy_mode === 'MASTER_MANUAL';
  const { data: mappingsData } = useMasterStrategyMappings(strategyId);
  const createMut = useCreateMasterMapping(strategyId);
  const updateMut = useUpdateMasterMapping(strategyId);
  const [rowErr, setRowErr] = useState('');

  const mappings = (mappingsData as any)?.mappings ?? [];
  const active = mappings.find((m: any) => m.mapped_user_id === userId && m.status !== 'REMOVED');
  const isMapped = !!active;
  const busy = createMut.isPending || updateMut.isPending;

  const handleToggle = async () => {
    setRowErr('');
    try {
      if (isMapped) {
        await updateMut.mutateAsync({ mapping_id: active.id, payload: { status: 'REMOVED' } });
        toast.success(`Unmapped "${strategy.name}"`);
      } else {
        await createMut.mutateAsync([{ mapped_user_id: userId, execution_mode: 'PAPER', qty_mode: 'BY_ALERT', qty_value: 1 }]);
        toast.success(`Mapped "${strategy.name}"`);
      }
    } catch (e: any) {
      setRowErr(e?.message || 'Update failed');
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-bg-border bg-bg-surface/40">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-txt-primary truncate">{strategy.name}</span>
          <span className="text-2xs font-mono text-txt-muted/70">#{strategyId}</span>
          <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-amber/10 border border-amber/30 text-amber">
            {isManualMaster ? 'Manual Master' : 'External Master'}
          </span>
        </div>
        {rowErr && <p className="text-2xs text-bear mt-0.5">{rowErr}</p>}
      </div>
      <label className="flex items-center gap-2 shrink-0 cursor-pointer">
        {busy && <Spinner size={12} />}
        <input
          type="checkbox"
          checked={isMapped}
          disabled={busy}
          onChange={handleToggle}
          className="accent-amber w-4 h-4"
        />
      </label>
    </div>
  );
}

// ─── Mark Flat operator-recovery decision dialog ───────────────────────────────
// Transient, Super Admin-only decision dialog for the Mark Flat recovery flow.
// All content derives from the normalized backend decision response — never
// from provider payloads or frontend-inferred broker evidence. "Do Nothing" is
// a UI-only close: it sends no request of any kind and triggers no mutation.
export function MarkFlatDialog({
  dialog,
  onChoose,
  onClose,
  isPending,
}: {
  dialog: {
    deploymentId: number;
    strategyName: string | null;
    response: any;
    pendingAction: string | null;
    error: string | null;
  } | null;
  onChoose: (action: string) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  if (!dialog) return null;
  const res = dialog.response ?? {};
  const routed = resolveMarkFlatResponse(res);
  const showCleanupIncomplete = routed.kind === 'cleanup_incomplete';
  const showCleanupInProgress = routed.kind === 'cleanup_in_progress';
  // Strictly the backend-returned requestable actions — cleanup is never
  // inferred from frontend data (e.g. SESSION_UNAVAILABLE offers none).
  const actions = requestableActions(res.available_actions);
  const node = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="card w-full max-w-md animate-fade-in shadow-panel flex flex-col max-h-[calc(100dvh-2rem)] my-auto">
        <div className="p-6 pb-3 shrink-0">
          <h3 className="font-display font-semibold text-txt-primary">Mark Position Flat</h3>
          <p className="text-2xs font-mono text-txt-muted mt-0.5">
            {dialog.strategyName || 'Deployment'} — Dep #{dialog.deploymentId}
          </p>
        </div>
        <div className="px-6 overflow-y-auto space-y-3">
          {showCleanupIncomplete && (
            <p className="text-xs text-warn bg-warn/10 border border-warn/30 rounded px-3 py-2">
              {CLEANUP_INCOMPLETE_NOTICE}
            </p>
          )}
          {showCleanupInProgress && (
            <p className="text-xs text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded px-3 py-2">
              {CLEANUP_IN_PROGRESS_NOTICE}
            </p>
          )}
          <p className="text-xs text-txt-secondary">{verificationNotice(res.verification_status, res)}</p>
        </div>
        <div className="p-6 pt-4 shrink-0 space-y-2">
          {actions.map((action: string) => (
            <button
              key={action}
              onClick={() => onChoose(action)}
              disabled={isPending}
              className={`w-full text-left rounded border px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                action === 'CLEANUP_THEN_MARK_FLAT'
                  ? 'border-bear/50 bg-bear/10 text-bear hover:bg-bear/20'
                  : 'border-warn/40 bg-warn/5 text-warn hover:bg-warn/10'
              }`}
            >
              <span className="text-xs font-semibold flex items-center gap-1.5">
                {dialog.pendingAction === action && <Spinner size={12} />}
                {MARK_FLAT_ACTION_LABELS[action as keyof typeof MARK_FLAT_ACTION_LABELS]}
              </span>
              <span className="block text-2xs text-txt-muted mt-0.5 leading-relaxed">
                {MARK_FLAT_ACTION_EXPLANATIONS[action as keyof typeof MARK_FLAT_ACTION_EXPLANATIONS]}
              </span>
            </button>
          ))}
          <button
            onClick={onClose}
            disabled={isPending}
            className="btn-ghost w-full text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {MARK_FLAT_ACTION_LABELS.DO_NOTHING}
          </button>
          {dialog.error && (
            <p className="text-2xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">
              {dialog.error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}

// ─── Operator own-account notice ───────────────────────────────────────────────
// An Operator's own identity is not managed through the administrative page
// (the backend also rejects it); password and business settings have their own
// self-service pages.
function OperatorSelfAccountNotice() {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl space-y-5">
      <button onClick={() => navigate(ROUTES.ADMIN_USERS)} className="btn-ghost flex items-center gap-2">
        <ArrowLeft size={14} /> Users
      </button>
      <div className="panel p-6 space-y-3" data-testid="operator-self-account-notice">
        <div className="flex items-center gap-2 text-txt-primary">
          <AlertCircle size={16} className="text-warn" />
          <p className="text-sm font-semibold">Your own account is not managed here</p>
        </div>
        <p className="text-xs text-txt-muted">
          User Management lists only your assigned customers. Use your Profile to change your password,
          and Branding Settings or Payment Gateway for your business settings.
        </p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate(ROUTES.PROFILE)} className="btn-primary text-xs">Profile</button>
          <button onClick={() => navigate(ROUTES.CHANGE_PASSWORD)} className="btn-ghost text-xs">Change Password</button>
          <button onClick={() => navigate(ROUTES.OPERATOR_BRANDING_SETTINGS)} className="btn-ghost text-xs">Branding Settings</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const sessionRole = useSessionStore((s) => s.role);
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const viewerId = useSessionStore((s) => s.user?.id ?? null);
  // Checked before the detail content mounts so no administrative request is
  // made for the Operator's own id (e.g. from an old bookmarked URL).
  if (!isSuperAdmin && sessionRole === 'operator' && viewerId != null && Number(id) === viewerId) {
    return <OperatorSelfAccountNotice />;
  }
  return <AdminUserDetailContent />;
}

function AdminUserDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId   = Number(id);

  const { data: user, isLoading, error } = useAdminIdentityUser(userId);
  const { data: roles = [] }             = useAdminRoles();
  const assignRole  = useAssignRole();
  const removeRole  = useRemoveRole();
  const setStatus   = useSetUserStatus();
  const deleteUser  = useDeleteUser();
  const isSuperAdmin   = useSessionStore((s) => s.isSuperAdmin);
  const sessionRole    = useSessionStore((s) => s.role);
  const viewerRawRoles = useSessionStore((s) => s.user?.roles ?? []);
  const viewerId       = useSessionStore((s) => s.user?.id ?? null);
  const canEditValidity = isSuperAdmin || sessionRole === 'admin' || viewerRawRoles.includes('admin') || viewerRawRoles.includes('super_admin');
  const { data: me }   = useMe();
  // Must stay above the loading/error early returns (Rules of Hooks).
  const qc = useQueryClient();

  // Broker: graceful — 403/404 just means no access or not mapped
  const { data: broker, error: brokerErr } = useBrokerCurrent(userId);
  const brokerName = broker?.has_active_broker ? broker?.broker_name : null;
  const { data: brokerProxyBinding } = useBrokerProxyBinding(
    brokerName ? { user_id: userId, broker_name: brokerName } : null,
    { enabled: Boolean(brokerName) },
  );
  // Explicit target-user id — never the logged-in admin's own availability.
  const {
    data: brokerAvailabilityData,
    isLoading: brokerAvailabilityLoading,
    isError: brokerAvailabilityIsError,
  } = useBrokerMappingAvailability(userId);
  const brokerAvailabilityMap = computeBrokerAvailabilityMap({
    brokerNames: BROKER_NAMES,
    availability: brokerAvailabilityData,
    isLoading: brokerAvailabilityLoading,
    isError: brokerAvailabilityIsError,
  });
  // Contacts for the managed target user only, requested once the backend has
  // authorized and returned this exact target's profile.
  const supportContact = useSupportContact(userId, {
    enabled: Number.isInteger(userId) && userId > 0 && user?.id === userId,
  });
  const mapBroker   = useMapBroker();
  const unmapBroker = useUnmapBroker();
  const [showBrokerForm, setShowBrokerForm] = useState(false);
  const [brokerForm, setBrokerForm] = useState({ broker_name: '', broker_client_id: '', totp_key: '', credentials: {} as Record<string, string> });
  const [brokerMapErr, setBrokerMapErr] = useState('');

  // Strategies for this user (gated on backend by allow_admin_access)
  const { data: strategies = [], error: stratErr } = useAdminStrategiesByUser(userId);

  // Position status
  const positionSummary = useAdminPositionSummary(userId);
  const markFlat = useMarkDeploymentFlat();
  const dismissOrder = useDismissOrder();
  const [flatErrors, setFlatErrors] = useState<Record<number, string>>({});
  // Mark Flat operator-recovery flow. The controller owns the exact mutation
  // variable shapes and the stable idempotency-key lifecycle (component memory
  // only — keys are never persisted, displayed, or logged).
  const markFlatCtl = useMemo(() => createMarkFlatController(), []);
  const [markFlatDialog, setMarkFlatDialog] = useState<{
    deploymentId: number;
    strategyName: string | null;
    response: any;
    pendingAction: string | null;
    error: string | null;
  } | null>(null);

  // Route a mark-flat backend response. MARKED_FLAT closes with the legacy
  // success confirmation; DECISION_REQUIRED / CLEANUP_* keep the
  // transient decision dialog, driven only by the normalized response. Any
  // terminal outcome ends the deployment's current recovery episode and
  // clears all of its stored explicit-action idempotency keys, so a later
  // episode can never replay a stale historical terminal operation.
  const applyMarkFlatOutcome = (
    deploymentId: number,
    strategyName: string | null,
    res: any,
  ) => {
    const routed = resolveMarkFlatResponse(res);
    markFlatCtl.finish(deploymentId, res);
    if (routed.kind === 'success') {
      setMarkFlatDialog(null);
      toast.success(`Dep #${deploymentId} marked FLAT`);
      return;
    }
    if (
      routed.kind === 'decision'
      || routed.kind === 'cleanup_incomplete'
      || routed.kind === 'cleanup_in_progress'
    ) {
      setMarkFlatDialog({
        deploymentId,
        strategyName,
        response: res,
        pendingAction: null,
        error: null,
      });
      return;
    }
    // Unknown 2xx outcome — never guess and never expose the raw response.
    // Surface the bounded safe notice in the open dialog for this
    // deployment, or on the deployment row when no dialog exists (initial
    // INSPECT).
    if (markFlatDialog && markFlatDialog.deploymentId === deploymentId) {
      setMarkFlatDialog((d) => (d ? { ...d, pendingAction: null, error: UNKNOWN_OUTCOME_NOTICE } : d));
    } else {
      setFlatErrors((e) => ({ ...e, [deploymentId]: UNKNOWN_OUTCOME_NOTICE }));
    }
  };

  const handleMarkFlatClick = async (dep: any) => {
    if (markFlat.isPending) return;
    setFlatErrors((e) => ({ ...e, [dep.deployment_id]: '' }));
    try {
      // Initial click is INSPECT only: no action body, no Idempotency-Key.
      const res = await markFlat.mutateAsync(markFlatCtl.inspect(dep.deployment_id));
      applyMarkFlatOutcome(dep.deployment_id, dep.strategy_name, res);
    } catch (err: any) {
      // Interceptor-normalized error (message/status/data) — never raw payloads.
      setFlatErrors((e) => ({ ...e, [dep.deployment_id]: markFlatErrorMessage(err) }));
    }
  };

  const handleMarkFlatDialogAction = async (action: string) => {
    if (!markFlatDialog || markFlat.isPending) return;
    const { deploymentId, strategyName } = markFlatDialog;
    let vars;
    try {
      vars = markFlatCtl.begin(deploymentId, action);
    } catch {
      setMarkFlatDialog((d) =>
        d ? { ...d, error: 'A secure recovery key could not be generated. Please try again.' } : d,
      );
      return;
    }
    setMarkFlatDialog((d) => (d ? { ...d, pendingAction: action, error: null } : d));
    try {
      const res = await markFlat.mutateAsync(vars);
      applyMarkFlatOutcome(deploymentId, strategyName, res);
    } catch (err: any) {
      // Uncertain/failed operation: the same key is intentionally retained
      // so retrying this action resumes the exact same backend operation.
      setMarkFlatDialog((d) => (d ? { ...d, pendingAction: null, error: markFlatErrorMessage(err) } : d));
    }
  };

  // "Do Nothing": UI-only close — zero requests, zero backend mutation. It
  // abandons the current frontend recovery episode and clears the stored
  // idempotency keys so a later episode starts fresh. If an earlier uncertain
  // operation is actually still IN_PROGRESS, the backend's
  // one-IN_PROGRESS-per-deployment invariant safely rejects a new key.
  const handleMarkFlatDialogClose = () => {
    if (markFlatDialog) markFlatCtl.abandon(markFlatDialog.deploymentId);
    setMarkFlatDialog(null);
  };

  // Subscription
  const { data: activeSub } = useAdminUserSubscription(userId);
  const { data: subHistory = [] } = useAdminUserSubscriptionHistory(userId);
  const { data: adminPlans = [] } = useAdminBillingPlans();
  const assignPlanMutation = useAssignPlan();
  const updateUserValidity = useUpdateUserValidity();
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assignPlanErr, setAssignPlanErr] = useState('');
  const [editingValidity, setEditingValidity] = useState(false);
  const [validityDate, setValidityDate] = useState('');
  const [validityErr, setValidityErr] = useState('');
  const walletOpId = activeSub?.operator_id ?? user?.operator_id;
  const { data: wallet } = useOperatorWallet(walletOpId);

  // License wallet for operator users
  const targetIsOperator = (user?.roles ?? []).includes('operator');
  const { data: opWallet } = useOperatorWallet(targetIsOperator ? userId : null);
  const { data: licensePacks = [] } = useLicensePacks({ include_inactive: false });
  const assignLicensePack = useAssignLicensePack();
  const [assignPackId, setAssignPackId] = useState('');
  const [assignPackErr, setAssignPackErr] = useState('');

  const handleAssignLicensePack = async () => {
    if (!assignPackId) return;
    setAssignPackErr('');
    try {
      await assignLicensePack.mutateAsync({ packId: Number(assignPackId), body: { operator_id: userId } });
      const pack = (licensePacks as any[]).find((p: any) => p.id === Number(assignPackId));
      toast.success(`"${pack?.name}" assigned — ${pack?.license_qty} licenses credited`);
      setAssignPackId('');
    } catch (e: any) {
      setAssignPackErr(e?.message || 'Failed to assign license pack.');
    }
  };

  const [selectedRole, setSelectedRole] = useState('');
  const [roleErr, setRoleErr]           = useState('');
  const [statusErr, setStatusErr]       = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteErr, setDeleteErr]       = useState('');
  const [deleteMode, setDeleteMode]     = useState<'soft' | 'hard'>('soft');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editName, setEditName]         = useState('');
  const [editEmail, setEditEmail]       = useState('');
  const [editPhone, setEditPhone]       = useState('');
  const [editProfileErr, setEditProfileErr] = useState('');
  const updateProfile = useUpdateUserProfile();
  const reassignOp = useReassignOperator();

  // 404 → redirect cleanly instead of showing a dead-end error panel
  useEffect(() => {
    if (error && (error as any)?.status === 404) {
      toast.warning('User not found or has been deleted');
      navigate(ROUTES.ADMIN_USERS, { replace: true });
    }
  }, [error, navigate]);

  useEffect(() => {
    setValidityDate(user?.valid_till ? String(user.valid_till).slice(0, 10) : '');
  }, [user?.valid_till]);

  const handleAssignRole = async () => {
    if (!selectedRole) return;
    setRoleErr('');
    try {
      await assignRole.mutateAsync({ userId, role: selectedRole });
      toast.success(`Role ${selectedRole} assigned`);
      setSelectedRole('');
    } catch (e: any) { setRoleErr(e?.message || 'Failed to assign role.'); }
  };

  const handleRemoveRole = async (role: string) => {
    setRoleErr('');
    try {
      await removeRole.mutateAsync({ userId, roleNameOrId: role });
      toast.success(`Role ${role} removed`);
    } catch (e: any) { setRoleErr(e?.message || 'Failed to remove role.'); }
  };

  const handleStatusChange = async (newStatus: string) => {
    setStatusErr('');
    try {
      await setStatus.mutateAsync({ id: userId, status: newStatus });
      toast.success(`User status set to ${newStatus}`);
    } catch (e: any) { setStatusErr(e?.message || 'Failed to update status.'); }
  };

  const handleDelete = async () => {
    setDeleteErr('');
    try {
      const res: any = await deleteUser.mutateAsync({ id: userId, mode: deleteMode });
      if (res?.delete_mode === 'hard' && res?.purged === true) {
        const counts = res.deleted_counts as Record<string, number> | undefined;
        const summary = counts
          ? Object.entries(counts)
              .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ')}: ${v}`)
              .join(', ')
          : null;
        toast.success(`User permanently deleted${summary ? ` (${summary})` : ''}`);
      } else {
        toast.success('User deleted');
      }
      navigate(ROUTES.ADMIN_USERS, { replace: true });
    } catch (e: any) {
      if ((e as any)?.status === 403) {
        setDeleteErr('You do not have permission to delete this user.');
      } else {
        setDeleteErr(e?.message || 'Failed to delete user.');
      }
    }
  };

  const handleAssignPlan = async () => {
    if (!assignPlanId) return;
    setAssignPlanErr('');
    try {
      await assignPlanMutation.mutateAsync({ uid: userId, body: { plan_id: Number(assignPlanId) } });
      toast.success('Plan assigned');
      setAssignPlanId('');
    } catch (e: any) {
      setAssignPlanErr(e?.message || 'Failed to assign plan.');
    }
  };

  const handleUpdateValidity = async () => {
    if (!validityDate) return;
    setValidityErr('');
    try {
      await updateUserValidity.mutateAsync({ uid: userId, body: { valid_till: validityDate } });
      toast.success('Validity updated');
      setEditingValidity(false);
    } catch (e: any) {
      setValidityErr(e?.message || 'Failed to update validity.');
    }
  };

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (error) {
    // 404 is handled by useEffect above; show generic panel for other errors
    if ((error as any)?.status === 404) return null;
    return (
      <div className="max-w-2xl">
        <button onClick={() => navigate(ROUTES.ADMIN_USERS)} className="btn-ghost mb-4 flex items-center gap-2">
          <ArrowLeft size={14} /> Back to Users
        </button>
        <div className="panel p-6 flex items-center gap-3 text-bear">
          <AlertCircle size={18} />
          <p className="text-sm">Failed to load user. {(error as any)?.message}</p>
        </div>
      </div>
    );
  }

  const currentRoleNames: string[] = user?.roles || [];

  // Resolve operator display name from cached user list (no extra fetch)
  const cachedUsers: any[] = qc.getQueryData(['adminIdentityUsers', viewerId]) ?? [];
  const operatorUser = user?.operator_id != null
    ? cachedUsers.find((u: any) => u.id === user.operator_id)
    : null;
  const operatorLabel = operatorUser
    ? `${operatorUser.full_name || operatorUser.username}${operatorUser.email ? ` (${operatorUser.email})` : ''}`
    : user?.operator_id != null ? `#${user.operator_id}` : null;
  const operatorList = cachedUsers.filter((u: any) => (u.roles || []).includes('operator'));

  const isOperatorViewer = sessionRole === 'operator';
  const adminAccessGranted = !!user?.allow_admin_access || isSuperAdmin;

  // Non-super_admin cannot assign or remove the super_admin role
  const availableToAdd = roles
    .filter((r: any) => !currentRoleNames.includes(r.name))
    .filter((r: any) => isSuperAdmin || r.name !== 'super_admin');

  // Delete UI visibility: viewer × target role matrix
  const targetHasAdmin    = currentRoleNames.some((r) => ['admin', 'super_admin'].includes(r));
  const targetHasOperPlus = currentRoleNames.some((r) => ['operator', 'admin', 'super_admin'].includes(r));
  let canDelete = false;
  if (isSuperAdmin) {
    canDelete = true;
  } else if (sessionRole === 'admin') {
    canDelete = !targetHasAdmin;       // admin viewer: end_user + operator targets
  } else if (viewerRawRoles.includes('operator')) {
    // operator viewer: end_user targets only AND allow_user_delete must be set on their limits
    canDelete = !targetHasOperPlus && me?.operator_limits?.allow_user_delete === true;
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(ROUTES.ADMIN_USERS)} className="btn-ghost flex items-center gap-2">
          <ArrowLeft size={14} /> Users
        </button>
        <SectionHeader title={user?.full_name || user?.username || `User #${userId}`} subtitle={`@${user?.username}`} />
      </div>

      {/* ── Profile Card ── */}
      <div className="panel p-5 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-amber/20 flex items-center justify-center">
            <User size={18} className="text-amber" />
          </div>
          <div>
            <p className="font-semibold text-txt-primary">{user?.full_name || '—'}</p>
            <p className="text-xs font-mono text-txt-muted">{user?.email || '—'}</p>
          </div>
          <div className="ml-auto"><StatusBadge status={user?.status} /></div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-bg-border">
          <div>
            <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">User ID</p>
            <p className="font-mono text-txt-secondary mt-0.5">{user?.id}</p>
          </div>
          <div>
            <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Phone</p>
            <p className="font-mono text-txt-secondary mt-0.5">{user?.phone || '—'}</p>
          </div>
          <div>
            <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Email Verified</p>
            <p className="mt-0.5 flex items-center gap-1">
              {user?.email_verified
                ? <><CheckCircle size={12} className="text-bull" /><span className="text-bull">Yes</span></>
                : <><XCircle size={12} className="text-txt-muted" /><span className="text-txt-muted">No</span></>}
            </p>
          </div>
          <div>
            <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Joined</p>
            <p className="font-mono text-txt-secondary mt-0.5">{fmtDateTime(user?.created_at)}</p>
          </div>
          <div>
            <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Admin Access</p>
            <p className="mt-0.5 flex items-center gap-1">
              {user?.allow_admin_access
                ? <><ShieldCheck size={12} className="text-bull" /><span className="text-bull">Enabled</span></>
                : <><XCircle size={12} className="text-txt-muted" /><span className="text-txt-muted">Disabled</span></>}
            </p>
          </div>
          {(operatorLabel || (isSuperAdmin && currentRoleNames.includes('end_user'))) && (
            <div className="col-span-2">
              <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Operator</p>
              {isSuperAdmin && currentRoleNames.includes('end_user') ? (
                <div className="flex items-center gap-2 mt-1">
                  <select
                    className="input-base text-xs flex-1"
                    value={user?.operator_id ?? ''}
                    onChange={async (e) => {
                      const newOpId = Number(e.target.value);
                      if (!newOpId || newOpId === user?.operator_id) return;
                      try {
                        await reassignOp.mutateAsync({ userId, operatorId: newOpId });
                        toast.success('Operator reassigned');
                      } catch (err: any) { toast.error(err?.message || 'Failed to reassign'); }
                    }}
                    disabled={reassignOp.isPending}
                  >
                    <option value="">— Select operator —</option>
                    {operatorList.map((op: any) => (
                      <option key={op.id} value={op.id}>
                        {op.full_name || op.username}{op.email ? ` (${op.email})` : ''}
                      </option>
                    ))}
                  </select>
                  {reassignOp.isPending && <span className="text-2xs text-txt-muted">Saving…</span>}
                </div>
              ) : (
                <p className="font-mono text-txt-secondary mt-0.5">{operatorLabel}</p>
              )}
            </div>
          )}
        </div>

        {/* Edit Profile */}
        <div className="pt-2 border-t border-bg-border">
          {!showEditProfile ? (
            <button
              className="btn-ghost text-xs flex items-center gap-1.5"
              onClick={() => {
                setEditName(user?.full_name || '');
                setEditEmail(user?.email || '');
                setEditPhone(user?.phone || '');
                setEditProfileErr('');
                setShowEditProfile(true);
              }}
            >
              <Pencil size={13} /> Edit Profile
            </button>
          ) : (
            <div className="rounded border border-bg-border bg-bg-hover/20 p-4 space-y-3">
              <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Edit Profile</p>
              <div className="space-y-2">
                <div>
                  <label className="text-2xs text-txt-muted mb-1 block">Full Name</label>
                  <input
                    className="input-base text-xs w-full"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="text-2xs text-txt-muted mb-1 block">Email</label>
                  <input
                    className="input-base text-xs w-full"
                    type="email"
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    placeholder="Email address"
                  />
                </div>
                <div>
                  <label className="text-2xs text-txt-muted mb-1 block">Phone</label>
                  <input
                    className="input-base text-xs w-full"
                    value={editPhone}
                    onChange={e => setEditPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              {editProfileErr && <p className="text-xs text-bear">{editProfileErr}</p>}
              <div className="flex gap-2">
                <button
                  className="btn-primary text-xs"
                  disabled={updateProfile.isPending}
                  onClick={async () => {
                    setEditProfileErr('');
                    const payload: Record<string, string> = {};
                    if (editName.trim() !== (user?.full_name || '')) payload.full_name = editName.trim();
                    if (editEmail.trim() !== (user?.email || '')) payload.email = editEmail.trim();
                    if (editPhone.trim() !== (user?.phone || '')) payload.phone = editPhone.trim();
                    if (!Object.keys(payload).length) { setShowEditProfile(false); return; }
                    try {
                      await updateProfile.mutateAsync({ id: userId, ...payload });
                      toast.success('Profile updated');
                      setShowEditProfile(false);
                    } catch (e: any) {
                      setEditProfileErr(e?.message || 'Failed to update profile');
                    }
                  }}
                >
                  {updateProfile.isPending ? 'Saving…' : 'Save'}
                </button>
                <button className="btn-ghost text-xs" onClick={() => setShowEditProfile(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Status segmented control */}
        <div className="pt-2 border-t border-bg-border space-y-2">
          <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Change Status</p>
          <div className={`inline-flex rounded border border-bg-border overflow-hidden ${setStatus.isPending ? 'opacity-50 pointer-events-none' : ''}`}>
            {(['active', 'blocked'] as const).map((s, i) => (
              <button
                key={s}
                onClick={() => user?.status !== s && handleStatusChange(s)}
                disabled={setStatus.isPending || user?.status === s}
                className={`text-xs px-4 py-1.5 transition-colors ${i > 0 ? 'border-l border-bg-border' : ''} ${
                  user?.status === s
                    ? s === 'active' ? 'bg-bull/20 text-bull font-medium' : 'bg-bear/20 text-bear font-medium'
                    : 'text-txt-muted hover:bg-bg-hover'
                }`}
              >
                {s === 'active' ? 'Active' : 'Blocked'}
              </button>
            ))}
          </div>
          {setStatus.isPending && <p className="text-2xs text-txt-muted">Updating…</p>}
          {statusErr && <p className="text-xs text-bear">{statusErr}</p>}
        </div>
      </div>

      {/* ── Roles (hidden for operators — they can only create end_users) ── */}
      {!isOperatorViewer && <div className="panel p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-amber" />
          <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Roles</h3>
        </div>

        <div className="flex flex-wrap gap-2">
          {currentRoleNames.length === 0 && <span className="text-xs text-txt-muted">No roles assigned.</span>}
          {currentRoleNames.map((r) => (
            <RoleBadge
              key={r}
              role={r}
              onRemove={
                // Non-super_admin cannot remove the super_admin role
                (isSuperAdmin || r !== 'super_admin') ? () => handleRemoveRole(r) : undefined
              }
            />
          ))}
        </div>

        {availableToAdd.length > 0 && (
          <div className="flex gap-2 items-center pt-1">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="input-base flex-1 text-xs"
            >
              <option value="">Select role to assign…</option>
              {availableToAdd.map((r: any) => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
            <button
              onClick={handleAssignRole}
              disabled={!selectedRole || assignRole.isPending}
              className="btn-primary flex items-center gap-1.5"
            >
              <Plus size={12} /> Assign
            </button>
          </div>
        )}
        {roleErr && <p className="text-xs text-bear">{roleErr}</p>}
      </div>}

      {/* ── Strategy Mapping (operator/admin/super-admin) ── */}
      {(isOperatorViewer || canEditValidity) && (
        <StrategyMappingSection userId={userId} />
      )}

      {/* ── Broker ── */}
      <div className="panel p-5 space-y-3">
        <div className="flex items-center gap-2">
          <LinkIcon size={14} className="text-amber" />
          <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Broker</h3>
        </div>
        {brokerErr ? (
          <p className="text-xs text-txt-muted flex items-center gap-2">
            <AlertTriangle size={13} className="text-warn" />
            Broker data unavailable — {(brokerErr as any)?.status === 403 ? 'insufficient permissions' : 'no mapping found'}.
          </p>
        ) : !broker || broker.has_active_broker === false ? (
          <>
            <p className="text-xs text-txt-muted">No broker mapped.</p>
            {adminAccessGranted && (
              !showBrokerForm ? (
                <button onClick={() => setShowBrokerForm(true)} className="btn-primary text-xs flex items-center gap-1.5">
                  <Plus size={12} /> Map Broker
                </button>
              ) : (
                <div className="space-y-3 pt-2 border-t border-bg-border">
                  <div className="space-y-1">
                    <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Broker</label>
                    <select
                      value={brokerForm.broker_name}
                      onChange={(e) => setBrokerForm((f) => ({ ...f, broker_name: e.target.value, credentials: {} }))}
                      className="input-base text-xs"
                      disabled={mapBroker.isPending || brokerAvailabilityLoading || brokerAvailabilityIsError}
                    >
                      <option value="">Select broker…</option>
                      {BROKER_NAMES.map((b) => (
                        <option key={b} value={b} disabled={!isBrokerSelectable(brokerAvailabilityMap, b)}>
                          {BROKER_CONFIGS[b]?.label ?? b}
                        </option>
                      ))}
                    </select>
                    {brokerAvailabilityLoading && (
                      <p className="text-2xs text-txt-muted">{BROKER_AVAILABILITY_LOADING_MESSAGE}</p>
                    )}
                    {!brokerAvailabilityLoading && brokerAvailabilityIsError && (
                      <p className="text-2xs text-bear">{BROKER_AVAILABILITY_ERROR_MESSAGE}</p>
                    )}
                    <BrokerSupportNotice
                      className="pt-1"
                      supportEmail={supportContact.data?.support_email}
                      supportPhone={supportContact.data?.support_phone}
                      isLoading={supportContact.isLoading}
                      isError={supportContact.isError}
                    />
                  </div>
                  {brokerForm.broker_name && (
                    <BrokerCredentialFields
                      brokerName={brokerForm.broker_name}
                      brokerClientId={brokerForm.broker_client_id}
                      totpKey={brokerForm.totp_key}
                      credentials={brokerForm.credentials}
                      onTopLevelChange={(field, value) => setBrokerForm((form) => ({
                        ...form,
                        [field]: value,
                      }))}
                      onCredentialChange={(key, value) => setBrokerForm((form) => ({
                        ...form,
                        credentials: { ...form.credentials, [key]: value },
                      }))}
                      disabled={mapBroker.isPending}
                      mode="map"
                      inputClassName="input-base text-xs"
                    />
                  )}
                  {brokerMapErr && <p className="text-xs text-bear">{brokerMapErr}</p>}
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => { setShowBrokerForm(false); setBrokerMapErr(''); }} className="btn-ghost text-xs">Cancel</button>
                    <button
                      type="button"
                      onClick={async () => {
                        setBrokerMapErr('');
                        if (brokerForm.broker_name === 'kotakneo' && !brokerForm.totp_key.trim()) {
                          setBrokerMapErr('TOTP Key is required for Kotak Neo.');
                          return;
                        }
                        const missingRequired = getBrokerFields(brokerForm.broker_name)
                          .filter((field) => field.requiredOnMap && !brokerForm.credentials[field.key]?.trim())
                          .map((field) => field.label);
                        if (missingRequired.length > 0) {
                          setBrokerMapErr(`Required credentials missing: ${missingRequired.join(', ')}.`);
                          return;
                        }
                        // userId must be sent separately so brokerApi.map routes it to the
                        // backend query param — the request body has no user_id field, so
                        // embedding it here would silently map to the admin's own account.
                        const payload: Record<string, any> = {
                          userId,
                          broker_name: brokerForm.broker_name.toUpperCase(),
                        };
                        if (
                          shouldRenderBrokerClientId(brokerForm.broker_name)
                          && brokerForm.broker_client_id.trim()
                        ) {
                          payload.broker_client_id = brokerForm.broker_client_id.trim();
                        }
                        if (brokerForm.totp_key.trim()) payload.totp_key = brokerForm.totp_key.trim();
                        if (Object.keys(brokerForm.credentials).length > 0) payload.credentials = brokerForm.credentials;
                        try {
                          await mapBroker.mutateAsync(payload);
                          toast.success('Broker mapped');
                          setShowBrokerForm(false);
                          setBrokerForm({ broker_name: '', broker_client_id: '', totp_key: '', credentials: {} });
                        } catch (e: any) { setBrokerMapErr(sanitizeBrokerMapErrorMessage(e)); }
                      }}
                      className="btn-primary text-xs"
                      disabled={
                        !brokerForm.broker_name
                        || mapBroker.isPending
                        || !isBrokerSelectable(brokerAvailabilityMap, brokerForm.broker_name)
                        || (brokerForm.broker_name === 'kotakneo' && !brokerForm.totp_key.trim())
                      }
                    >
                      {mapBroker.isPending ? 'Mapping…' : 'Map Broker'}
                    </button>
                  </div>
                </div>
              )
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ['Broker',    broker.broker_name],
                ['Client ID', broker.broker_client_id || '—'],
                ['Status',    broker.status],
                ['Mapped At', fmtDateTime(broker.mapped_at)],
                ['Static IP', brokerProxyBinding?.public_ip || '—'],
                ['IP Status', brokerProxyBinding?.status || 'Not assigned'],
              ].map(([label, val]) => (
                <div key={label as string}>
                  <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{label}</p>
                  <p className="font-mono text-txt-secondary mt-0.5">{val}</p>
                </div>
              ))}
              <BrokerCredentialsMasked broker={broker} />
            </div>
            {adminAccessGranted && (
              <button
                onClick={async () => {
                  if (!confirm('Unmap this broker? The user will need to re-map.')) return;
                  try {
                    await unmapBroker.mutateAsync({ userId, brokerName: broker.broker_name });
                    toast.success('Broker unmapped');
                  } catch (e: any) { toast.error(e?.message || 'Failed to unmap broker.'); }
                }}
                disabled={unmapBroker.isPending}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-bear/40 text-bear hover:bg-bear/10 transition-colors"
              >
                {unmapBroker.isPending ? 'Unmapping…' : 'Unmap Broker'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Strategies ── */}
      <div className="panel p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-amber" />
          <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Strategies</h3>
        </div>
        {!adminAccessGranted ? (
          <p className="text-xs text-txt-muted">This user has not enabled admin access. Strategies are hidden.</p>
        ) : stratErr ? (
          <p className="text-xs text-txt-muted flex items-center gap-2">
            <AlertTriangle size={13} className="text-warn" />
            {(stratErr as any)?.status === 403 ? 'User has not enabled admin access.' : 'Failed to load strategies.'}
          </p>
        ) : (strategies as any[]).length === 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-txt-muted">No strategies yet.</p>
            <button
              onClick={() => navigate(`/strategies/new?for_user=${userId}`)}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <Plus size={12} /> Create Strategy
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="border border-bg-border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-bg-surface text-txt-muted text-2xs font-mono uppercase tracking-widest">
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Updated</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(strategies as any[]).map((s: any) => (
                    <tr key={s.id} className="border-t border-bg-border hover:bg-bg-hover/50">
                      <td className="px-3 py-2 font-medium text-txt-primary">{s.name || s.strat_code || `#${s.id}`}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-2xs font-mono ${
                          s.status === 'active' ? 'bg-bull/10 text-bull' : 'bg-bg-surface text-txt-muted'
                        }`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-txt-muted font-mono">{fmtDate(s.updated_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => navigate(`/strategies/${s.id}/edit`)}
                          className="inline-flex items-center gap-1 text-2xs text-amber hover:text-amber-glow transition-colors"
                          title="Edit strategy"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={() => navigate(`/strategies/new?for_user=${userId}`)}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <Plus size={12} /> Create Strategy
            </button>
          </div>
        )}
      </div>

      {/* ── Position Status ── */}
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-txt-primary">Position Status</h3>
          <button
            onClick={() => positionSummary.refetch()}
            className="text-xs text-txt-muted hover:text-accent transition-colors"
            title="Refresh"
          >
            ↻ Refresh
          </button>
        </div>
        {positionSummary.isLoading ? (
          <Spinner size={16} />
        ) : positionSummary.isError ? (
          <p className="text-xs text-bear">Failed to load position data.</p>
        ) : !positionSummary.data?.length ? (
          <span className="inline-flex px-2 py-0.5 rounded text-xs bg-bull/10 text-bull border border-bull/30">
            All positions FLAT
          </span>
        ) : (
          <div className="space-y-3">
            {(positionSummary.data as any[]).map((dep: any) => (
              <div key={dep.deployment_id} className="rounded border border-accent/30 bg-accent/5 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-txt-primary">{dep.strategy_name}</span>
                    <span className="text-2xs text-txt-muted font-mono">Dep #{dep.deployment_id}</span>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-2xs font-mono ${
                      dep.deployment_status === 'ACTIVE' ? 'bg-bull/10 text-bull' : 'bg-bg-surface text-txt-muted'
                    }`}>{dep.deployment_status}</span>
                    <span className="inline-flex px-1.5 py-0.5 rounded text-2xs font-mono bg-bear/10 text-bear border border-bear/30">
                      {dep.position_state}
                    </span>
                  </div>
                  <button
                    disabled={markFlat.isPending && (markFlat.variables as any)?.deploymentId === dep.deployment_id}
                    onClick={() => handleMarkFlatClick(dep)}
                    className="btn-danger text-xs py-0.5 px-2 flex items-center gap-1 shrink-0"
                  >
                    {markFlat.isPending && (markFlat.variables as any)?.deploymentId === dep.deployment_id
                      ? <Spinner size={12} />
                      : 'Mark FLAT'}
                  </button>
                </div>
                {flatErrors[dep.deployment_id] && (
                  <p className="text-2xs text-bear mt-1">{flatErrors[dep.deployment_id]}</p>
                )}
                {dep.symbol_states?.filter((s: any) => s.position_status !== 'FLAT').length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {dep.symbol_states.filter((s: any) => s.position_status !== 'FLAT').map((s: any) => (
                      <div key={s.symbol} className="flex items-center gap-2 text-2xs text-txt-muted font-mono">
                        <span>{s.symbol}</span>
                        <span className={`px-1 rounded ${s.position_status === 'FLAT' ? 'text-bull' : 'text-bear'}`}>
                          {s.position_status}
                        </span>
                        {s.pyramid_qty > 0 && <span>qty={s.pyramid_qty}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {dep.order_issues?.length > 0 && (
                  <div className="mt-2 border-t border-accent/20 pt-2 space-y-1">
                    <p className="text-2xs text-txt-muted font-semibold uppercase tracking-wide">Order Issues</p>
                    {dep.order_issues.map((o: any) => (
                      <div key={o.order_id} className="flex items-start justify-between gap-2 bg-bg-surface rounded px-2 py-1">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-2xs font-mono text-txt-muted">#{o.order_id}</span>
                            <span className="text-2xs font-mono text-txt-primary">{o.symbol || '—'}</span>
                            <span className="inline-flex px-1 rounded text-2xs bg-warn/10 text-warn border border-warn/30">
                              {o.confirmation_status}
                            </span>
                            <span className="text-2xs text-txt-muted">{o.broker_name}</span>
                          </div>
                          {o.error_msg && (
                            <p className="text-2xs text-bear truncate max-w-xs" title={o.error_msg}>{o.error_msg}</p>
                          )}
                          <p className="text-2xs text-txt-muted font-mono">{o.placed_at ? new Date(o.placed_at).toLocaleString('en-IN') : ''}</p>
                        </div>
                        <button
                          disabled={dismissOrder.isPending && (dismissOrder.variables as any) === o.order_id}
                          onClick={() => dismissOrder.mutate(o.order_id)}
                          className="text-2xs text-txt-muted hover:text-bear transition-colors shrink-0 mt-0.5"
                          title="Dismiss"
                        >
                          {dismissOrder.isPending && (dismissOrder.variables as any) === o.order_id
                            ? <Spinner size={10} />
                            : <X size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Operator Limits ── */}
      <div className="panel p-5">
        <OperatorLimitsSection userId={userId} limits={user?.operator_limits} targetRoles={currentRoleNames} />
      </div>

      {/* ── Custom Domain Management (Super Admin viewing an Operator only) ── */}
      {isSuperAdmin && targetIsOperator && user?.id === userId && (
        <OperatorCustomDomainSection operatorId={userId} knownUsers={cachedUsers} />
      )}

      {/* ── License Wallet (operator users only) ── */}
      {targetIsOperator && (
        <div className="panel p-5 space-y-3">
          <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">License Wallet</h3>
          {opWallet ? (
            <div className="grid grid-cols-3 gap-3 text-center text-xs">
              {[
                { label: 'Total', value: opWallet.total_licenses, cls: 'text-txt-primary' },
                { label: 'Used', value: opWallet.used_licenses, cls: 'text-warn' },
                { label: 'Available', value: opWallet.total_licenses - opWallet.used_licenses, cls: opWallet.total_licenses - opWallet.used_licenses > 0 ? 'text-bull' : 'text-bear' },
              ].map(({ label, value, cls }) => (
                <div key={label} className="bg-bg-surface rounded p-2">
                  <div className={`text-lg font-bold ${cls}`}>{value}</div>
                  <div className="text-2xs text-txt-muted mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-txt-muted">No wallet yet (0 licenses).</p>
          )}

          {/* Assign license pack */}
          <div className="pt-2 border-t border-bg-border space-y-2">
            <p className="text-2xs text-txt-muted uppercase tracking-widest font-mono">Assign License Pack</p>
            {(licensePacks as any[]).filter((p: any) => p.is_active).length === 0 ? (
              <p className="text-xs text-txt-muted">No active license packs available. Create one in Plans &amp; Licenses.</p>
            ) : (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <select value={assignPackId} onChange={(e) => setAssignPackId(e.target.value)} className="input-base text-xs w-full">
                    <option value="">Select pack…</option>
                    {(licensePacks as any[]).filter((p: any) => p.is_active).map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.license_qty} license{p.license_qty !== 1 ? 's' : ''}
                        {p.price > 0 ? ` — ₹${p.price}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleAssignLicensePack}
                  disabled={!assignPackId || assignLicensePack.isPending}
                  className="btn-primary text-xs"
                >
                  {assignLicensePack.isPending ? 'Assigning…' : 'Assign'}
                </button>
              </div>
            )}
            {assignPackErr && <p className="text-xs text-bear">{assignPackErr}</p>}
          </div>
        </div>
      )}

      {/* ── Subscription ── */}
      <div className="panel p-5 space-y-3">
        <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Subscription</h3>

        <div className="space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Valid Till</p>
                {editingValidity ? (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <input
                      type="date"
                      value={validityDate}
                      onChange={(e) => setValidityDate(e.target.value)}
                      className="input-base text-xs max-w-[180px]"
                    />
                    <button
                      onClick={handleUpdateValidity}
                      disabled={!validityDate || updateUserValidity.isPending}
                      className="btn-primary text-2xs"
                    >
                      {updateUserValidity.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingValidity(false);
                        setValidityErr('');
                        setValidityDate(user?.valid_till ? String(user.valid_till).slice(0, 10) : '');
                      }}
                      className="btn-ghost text-2xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="mt-0.5 flex items-center gap-2">
                    <p className="font-mono text-txt-secondary">{user?.valid_till ? fmtDate(user.valid_till) : '—'}</p>
                    {canEditValidity && (
                      <button onClick={() => setEditingValidity(true)} className="text-2xs text-warn hover:text-warn/80">
                        Edit
                      </button>
                    )}
                  </div>
                )}
                {validityErr && <p className="text-xs text-bear mt-1">{validityErr}</p>}
              </div>
              <div>
                <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Source</p>
                <p className="font-mono text-txt-secondary mt-0.5">{activeSub?.source || '—'}</p>
              </div>
              {activeSub?.plan && (
                <div className="col-span-2">
                  <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Plan</p>
                  <p className="font-mono text-txt-secondary mt-0.5">{activeSub.plan.name} — {activeSub.plan.duration_months}m</p>
                </div>
              )}
            </div>

            {wallet && (
              <p className="text-2xs text-txt-muted">
                Operator wallet: <span className="text-txt-secondary">{wallet.total_licenses - wallet.used_licenses} / {wallet.total_licenses} licenses available</span>
              </p>
            )}
            {!activeSub && <p className="text-xs text-txt-muted">No active subscription.</p>}
          </div>

        {/* Assign plan */}
        <div className="pt-2 border-t border-bg-border space-y-2">
          <p className="text-2xs text-txt-muted uppercase tracking-widest font-mono">Assign Plan</p>
          {adminPlans.length === 0 ? (
            <p className="text-xs text-txt-muted">No active plans available.</p>
          ) : (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <select value={assignPlanId} onChange={(e) => setAssignPlanId(e.target.value)} className="input-base text-xs w-full">
                  <option value="">Select plan…</option>
                  {(adminPlans as any[]).filter((p: any) => p.is_active).map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.duration_months}m — {p.license_cost} license{p.license_cost !== 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleAssignPlan}
                disabled={!assignPlanId || assignPlanMutation.isPending}
                className="btn-primary text-xs"
              >
                {assignPlanMutation.isPending ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          )}
          {assignPlanErr && <p className="text-xs text-bear">{assignPlanErr}</p>}
        </div>

        {/* History (last 5) */}
        {(subHistory as any[]).length > 0 && (
          <div className="pt-2 border-t border-bg-border">
            <p className="text-2xs text-txt-muted uppercase tracking-widest font-mono mb-2">History</p>
            <div className="space-y-1">
              {(subHistory as any[]).slice(0, 5).map((row: any) => (
                <div key={row.id} className="flex items-center justify-between text-2xs text-txt-muted">
                  <span className="font-mono">{row.source}</span>
                  <span>{fmtDate(row.valid_from)} → {fmtDate(row.valid_till)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Danger Zone ── */}
      {canDelete && (
        <div className="panel p-5 space-y-3 border border-bear/20">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-bear" />
            <h3 className="text-xs font-mono text-bear uppercase tracking-widest">Danger Zone</h3>
          </div>
          <p className="text-xs text-txt-muted">
            Deactivate and remove this user from the platform. This is a soft delete — activity history is retained.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-bear/40 text-bear hover:bg-bear/10 transition-colors"
            >
              <Trash2 size={13} /> Delete User
            </button>
          ) : (
            <div className="space-y-3 rounded border border-bear/30 bg-bear/5 p-4">
              <p className="text-xs text-txt-secondary font-medium">
                Are you sure? This action deactivates the account and cannot be undone via the UI.
              </p>
              {/* Delete mode selector — super_admin only */}
              {isSuperAdmin && (
                <div className="space-y-1.5 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteMode"
                      checked={deleteMode === 'soft'}
                      onChange={() => setDeleteMode('soft')}
                      className="accent-amber"
                    />
                    <span className="text-txt-secondary">Soft delete — deactivate, retain data (default)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteMode"
                      checked={deleteMode === 'hard'}
                      onChange={() => setDeleteMode('hard')}
                      className="accent-bear"
                    />
                    <span className="text-bear">Permanently delete — purge all records. Cannot be undone.</span>
                  </label>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleDelete}
                  disabled={deleteUser.isPending}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-bear/50 bg-bear/20 text-bear hover:bg-bear/30 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={13} /> {deleteUser.isPending ? 'Deleting…' : 'Confirm Delete'}
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteErr(''); setDeleteMode('soft'); }}
                  disabled={deleteUser.isPending}
                  className="btn-ghost text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {deleteErr && (
            <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{deleteErr}</p>
          )}
        </div>
      )}

      {/* Mark Flat operator-recovery decision dialog (transient, portal-mounted) */}
      <MarkFlatDialog
        dialog={markFlatDialog}
        onChoose={handleMarkFlatDialogAction}
        onClose={handleMarkFlatDialogClose}
        isPending={markFlat.isPending || markFlatDialog?.pendingAction != null}
      />
    </div>
  );
}
